// src/Components/Security/SecurityPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../rbac/AuthContext";
import { getAllFlatsFromBlocks } from "../utils/flats";

const CATS = ["visitor", "delivery", "service"];

export default function SecurityPanel({ community }) {
  const { currentUser } = useAuth();
  const [members, setMembers] = useState([]); // {uid, name, email}
  const [loadingMembers, setLoadingMembers] = useState(true);

  const [form, setForm] = useState({
    visitorName: "",
    phone: "",
    category: "visitor",
    flatLabel: "",
    residentUid: "",
    purpose: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [visits, setVisits] = useState([]); // current visits for this community
  const [loadingVisits, setLoadingVisits] = useState(true);

  const flats = useMemo(
    () => getAllFlatsFromBlocks(community.blocks || []),
    [community.blocks],
  );

  // Load members data for resident pick
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMembers(true);
      try {
        const uids = Array.isArray(community.members) ? community.members : [];
        // fetch minimal profile
        const rows = [];
        for (const uid of uids) {
          try {
            const snap = await getDoc(doc(db, "users", uid));
            if (snap.exists()) {
              const d = snap.data();
              rows.push({
                uid,
                name: d.fullName || d.displayName || d.email || uid,
                email: d.email || "",
              });
            }
          } catch {
            /* ignore */
          }
        }
        if (!cancelled)
          setMembers(
            rows.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
          );
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load members.");
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [community.members]);

  // Live visits for this community
  useEffect(() => {
    setLoadingVisits(true);
    const qv = query(
      collection(db, "visits"),
      where("communityId", "==", community.id),
      orderBy("requestedAt", "desc"),
    );
    const unsub = onSnapshot(
      qv,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setVisits(rows);
        setLoadingVisits(false);
      },
      (err) => {
        console.error(err);
        setError(err?.message || "Failed to load visits.");
        setLoadingVisits(false);
      },
    );
    return () => unsub();
  }, [community.id]);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const createVisitRequest = async (e) => {
    e.preventDefault();
    setError("");
    const { visitorName, category, flatLabel, residentUid, purpose, phone } =
      form;
    if (!visitorName || !category || !flatLabel || !residentUid) {
      setError("Please fill Visitor Name, Category, Flat and Resident.");
      return;
    }
    try {
      setSaving(true);
      await addDoc(collection(db, "visits"), {
        communityId: community.id,
        visitorName: visitorName.trim(),
        phone: phone?.trim() || "",
        category,
        flatLabel,
        residentUid,
        purpose: purpose?.trim() || "",
        requestedBy: currentUser?.uid || null,
        status: "pending",
        requestedAt: serverTimestamp(),
      });
      setForm((prev) => ({ ...prev, visitorName: "", phone: "", purpose: "" }));
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to create request.");
    } finally {
      setSaving(false);
    }
  };

  const markEntered = async (v) => {
    try {
      await updateDoc(doc(db, "visits", v.id), {
        status: "entered",
        enteredAt: serverTimestamp(),
      });
    } catch (err) {
      alert(err?.message || "Failed to mark entry.");
    }
  };

  const markExited = async (v) => {
    try {
      await updateDoc(doc(db, "visits", v.id), {
        status: "exited",
        exitedAt: serverTimestamp(),
      });
    } catch (err) {
      alert(err?.message || "Failed to mark exit.");
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending: "warning",
      approved: "success",
      denied: "danger",
      entered: "primary",
      exited: "secondary",
    };
    return (
      <Badge bg={map[s] || "light"} className="text-capitalize">
        {s}
      </Badge>
    );
  };

  return (
    <Card>
      <Card.Header>
        <strong>Security Panel</strong>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="warning">{error}</Alert>}

        <Form onSubmit={createVisitRequest}>
          <div className="row g-2">
            <div className="col-12 col-md-3">
              <Form.Label>Visitor Name</Form.Label>
              <Form.Control
                name="visitorName"
                value={form.visitorName}
                onChange={onChange}
              />
            </div>
            <div className="col-12 col-md-3">
              <Form.Label>Phone (optional)</Form.Label>
              <Form.Control
                name="phone"
                value={form.phone}
                onChange={onChange}
              />
            </div>
            <div className="col-12 col-md-2">
              <Form.Label>Category</Form.Label>
              <Form.Select
                name="category"
                value={form.category}
                onChange={onChange}
              >
                {CATS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-12 col-md-2">
              <Form.Label>Flat</Form.Label>
              <Form.Select
                name="flatLabel"
                value={form.flatLabel}
                onChange={onChange}
              >
                <option value="">Select flat…</option>
                {flats.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-12 col-md-2">
              <Form.Label>Resident</Form.Label>
              <Form.Select
                name="residentUid"
                value={form.residentUid}
                onChange={onChange}
                disabled={loadingMembers}
              >
                <option value="">
                  {loadingMembers ? "Loading…" : "Select resident…"}
                </option>
                {members.map((m) => (
                  <option key={m.uid} value={m.uid}>
                    {m.name} ({m.email})
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-12">
              <Form.Label>Purpose (optional)</Form.Label>
              <Form.Control
                name="purpose"
                value={form.purpose}
                onChange={onChange}
                placeholder="Package delivery, AC repair…"
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? "Sending…" : "Send Approval Request"}
              </Button>
              <Form.Text className="ms-1">
                Resident will get a realtime notification with sound.
              </Form.Text>
            </div>
          </div>
        </Form>

        <hr />
        <h6>Requests</h6>
        {loadingVisits ? (
          <div className="d-flex align-items-center mt-2">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading…</span>
          </div>
        ) : (
          <Table responsive bordered hover size="sm" className="mt-2">
            <thead>
              <tr>
                <th>Visitor</th>
                <th>Flat</th>
                <th>Category</th>
                <th>Resident</th>
                <th>Status</th>
                <th style={{ width: 210 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visits.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No requests
                  </td>
                </tr>
              ) : (
                visits.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <div className="fw-semibold">{v.visitorName}</div>
                      <div className="text-muted">
                        <small>{v.phone || "—"}</small>
                      </div>
                    </td>
                    <td>
                      <code>{v.flatLabel}</code>
                    </td>
                    <td className="text-capitalize">{v.category}</td>
                    <td>
                      <code>{v.residentUid?.slice(0, 6)}…</code>
                    </td>
                    <td>{statusBadge(v.status)}</td>
                    <td className="d-flex flex-wrap gap-2">
                      {v.status === "approved" && (
                        <Button
                          size="sm"
                          onClick={() => markEntered(v)}
                          variant="outline-primary"
                        >
                          Mark Entry
                        </Button>
                      )}
                      {v.status === "entered" && (
                        <Button
                          size="sm"
                          onClick={() => markExited(v)}
                          variant="outline-secondary"
                        >
                          Mark Exit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
